import * as React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  FormHelperText,
  LinearProgress,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ButtonProps as MUIButtonProps } from "@mui/material/Button";
import BaseModal from "../../../components/global/BaseModal";
import { Talent } from "../../../types/users";
import TalentDrawer from "../../talent/TalentDrawer";
import {
  useApproveTalentWorkMutation,
  useForceCheckoutMutation,
  useGetReportProgressQuery,
} from "../../../services/projectJob.service";
import dayjs from "dayjs";
import {
  Project,
  ProjectResource,
  ProjectReview,
  ProjectStatus,
  ReportProgress,
  ReportProgressStatus,
} from "../../../types/project";
import ConfirmModal from "../../../components/ConfirmModal";
import { toast } from "react-toastify";
import {
  getHiredTalentStatus,
  getLastProgress,
} from "../../../utils/projectHelper";
import { useSubmitProjectReviewMutation } from "../../../services/review.service";
import Star from "@mui/icons-material/Star";
import ModalBonusForm from "../ModalBonusForm";
import ModalCancelTalent from "../ModalCancelTalent";
import ForceCheckoutModal from "./ForceCheckoutModal";
import GiveReviewModal from "./GiveReviewModal";
import LogModal from "./LogModal";
import PhotoModal from "./PhotoModal";
import { History, NotesOutlined, PhotoCamera } from "@mui/icons-material";
import ModalRejectJob from "../ModalRejectJob";
import { formatWithTimezone } from "../../../utils/dateHelper";
import { CardBase, GlassTable } from "../../../components/global";

const FinalNotesModal = ({
  open,
  onClose,
  notes,
}: {
  open: boolean;
  onClose: () => void;
  notes: string | null;
}) => {
  const footer = <Button onClick={onClose}>Close</Button>;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Final Notes"
      footer={footer}
      maxWidth="sm"
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            color: notes ? "text.primary" : "text.secondary",
            lineHeight: 1.7,
          }}
        >
          {notes || "No final notes were provided for this assignment."}
        </Typography>
      </Box>
    </BaseModal>
  );
};

interface ButtonPhotosProps {
  projectId: number;
  progressId: number;
  shouldRefresh?: boolean;
  buttonProps?: Omit<MUIButtonProps, "onClick" | "color" | "variant">;
}

const ButtonPhotos = ({
  projectId,
  progressId,
  shouldRefresh,
  buttonProps,
}: ButtonPhotosProps) => {
  const [photoVisible, setPhotoVisible] = React.useState(false);
  const theme = useTheme();
  const buttonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 1.75,
    justifyContent: "flex-start",
    minWidth: 160,
    borderColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.4 : 0.2),
    color: theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.main,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.mode === "dark"
        ? "0 4px 12px rgba(255, 255, 255, 0.08)"
        : "0 4px 12px rgba(0, 0, 0, 0.08)",
      borderColor: theme.palette.info.main,
    },
  } as const;
  const { data, isLoading, isFetching, isError, refetch } =
    useGetReportProgressQuery(
      {
        projectId,
        progressId,
      },
      {
        skip: !progressId,
      }
    );

  React.useEffect(() => {
    if (!!shouldRefresh) refetch();
  }, [shouldRefresh, refetch]);

  if (isLoading || isFetching || isError || !data?.documents?.length) {
    return null;
  }

  const mergedSx: SxProps<Theme> = buttonProps?.sx
    ? Array.isArray(buttonProps.sx)
      ? [buttonSx, ...buttonProps.sx]
      : [buttonSx, buttonProps.sx]
    : buttonSx;

  return (
    <>
      <Button
        color="info"
        variant="outlined"
        startIcon={<PhotoCamera />}
        onClick={() => setPhotoVisible(true)}
        sx={mergedSx}
        {...buttonProps}
      >
        Photos
      </Button>
      <PhotoModal
        open={photoVisible}
        documents={data?.documents}
        onClose={() => setPhotoVisible(false)}
      />
    </>
  );
};

const TableHireds = ({
  project,
  bids,
  reviews,
  loading,
}: {
  project: Project;
  bids: Array<ProjectResource>;
  reviews: Array<ProjectReview>;
  loading: boolean;
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedTalent, setSelectedTalent] = React.useState<Talent | null>(
    null
  );
  const [approveModal, setApproveModal] = React.useState<{
    visible: boolean;
    progress: ReportProgress | null;
  }>({
    visible: false,
    progress: null,
  });
  const [rejectModal, setRejectModal] = React.useState<{
    visible: boolean;
    progress: ReportProgress | null;
  }>({
    visible: false,
    progress: null,
  });
  const [logModal, setLogModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [reviewModal, setReviewModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [checkoutModal, setCheckoutModal] = React.useState<{
    visible: boolean;
    progress: ReportProgress | null;
  }>({
    visible: false,
    progress: null,
  });
  const [bonusModal, setBonusModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [cancelTalentModal, setCancelTalentModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [finalNotesModal, setFinalNotesModal] = React.useState<{
    visible: boolean;
    notes: string | null;
  }>({
    visible: false,
    notes: null,
  });

  const isHourly = React.useMemo(() => project?.type == "hourly", [project]);

  const theme = useTheme();
  const borderColor = theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.08)"
    : "rgba(0, 0, 0, 0.08)";
  const headerGradient = theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`;
  const rowHoverBg = theme.palette.mode === "dark"
    ? alpha(theme.palette.primary.main, 0.16)
    : alpha(theme.palette.primary.main, 0.08);
  const subtleTextColor = theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.7)"
    : "rgba(0, 0, 0, 0.6)";
  const actionButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    transition: "all 0.25s ease",
    boxShadow: "none",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
    [theme.breakpoints.down("sm")]: {
      px: 1,
      fontSize: "0.75rem",
      py: 0.5,
    },
  } as const;
  const textButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 1.5,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: rowHoverBg,
    },
    [theme.breakpoints.down("sm")]: {
      px: 1,
      fontSize: "0.75rem",
      py: 0.5,
    },
  } as const;
  const utilityButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 1.75,
    justifyContent: "flex-start",
    minWidth: 160,
    borderColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.4 : 0.2),
    color: theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.main,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.mode === "dark"
        ? "0 4px 12px rgba(255, 255, 255, 0.08)"
        : "0 4px 12px rgba(0, 0, 0, 0.08)",
      borderColor: theme.palette.info.main,
    },
    [theme.breakpoints.down("sm")]: {
      px: 1,
      fontSize: "0.75rem",
      py: 0.5,
      minWidth: 120,
    },
  } as const;
  const headCellSx = {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: { xs: "0.6rem", sm: "0.75rem" },
    letterSpacing: { xs: "0.04em", sm: "0.08em" },
    borderBottom: "none",
    color: "#12903C",
    py: { xs: 0.75, sm: 2 },
    px: { xs: 0.5, sm: 2 },
    lineHeight: { xs: 1.2, sm: 1.5 },
    whiteSpace: { xs: "normal", sm: "nowrap" },
    wordBreak: { xs: "break-word", sm: "normal" },
    overflow: { xs: "hidden", sm: "visible" },
    textOverflow: { xs: "ellipsis", sm: "clip" },
  } as const;

  const hiredCount = React.useMemo(() => bids?.length ?? 0, [bids]);

  const [doApprove] = useApproveTalentWorkMutation();
  const [doSubmitReview] = useSubmitProjectReviewMutation();
  const [doForceCheckout] = useForceCheckoutMutation();

  const handleApprove = async () => {
    try {
      const progress = approveModal.progress;
      if (!progress) {
        toast.error("Invalid");
        return;
      }
      await doApprove({
        projectId: project.id,
        progressId: progress.id,
      }).unwrap();
      toast.success("Independent Professional's work approved!");
    } catch (err: any) {
      toast.error(err);
    }
  };

  const handleReview = async (
    resource_id: number,
    star: number,
    message: string
  ) => {
    try {
      await doSubmitReview({
        resource_id,
        project_id: project.id,
        star,
        review: message,
      }).unwrap();
    } catch (err: any) {
      toast.error(err);
    }
  };

  const handleForceCheckout = async (
    progress_id: number,
    checkout_date: Date
  ) => {
    try {
      await doForceCheckout({
        projectId: project.id,
        progressId: progress_id,
        checkoutDate: checkout_date,
      }).unwrap();
      toast.success("Force checkout success");
    } catch (err: any) {
      toast.error(err);
    }
  };

  return (
    <Box>
      <CardBase
        title="Hired Independent Professionals"
        subtitle={
          hiredCount > 0
            ? `${hiredCount} independent professional${hiredCount === 1 ? "" : "s"} hired`
            : "No independent professionals hired yet"
        }
        useGlassCard={true}
        nested={true}
        sx={{
          width: "100%",
          p: 0,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 18px 48px rgba(0, 0, 0, 0.5)"
                : "0 18px 48px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Box sx={{ px: 0, py: { xs: 0, md: 3 } }}>
          {loading && (
            <LinearProgress
              sx={{
                mb: 3,
                height: 6,
                borderRadius: "999px",
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "& .MuiLinearProgress-bar": {
                  borderRadius: "999px",
                },
              }}
            />
          )}
          <GlassTable maxHeight={{ xs: 420, md: 560 }} hasData={hiredCount > 0}>
            <Table
              stickyHeader
              aria-label="hired-independent-professionals"
              sx={{
                minWidth: { xs: 300, sm: 650 },
                width: "100%",
                "& .MuiTableHead-root .MuiTableRow-root": {
                  background: headerGradient,
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  ...headCellSx,
                },
                "& .MuiTableBody-root .MuiTableCell-root": {
                  borderBottom: `1px solid ${borderColor}`,
                  fontSize: { xs: "0.7rem", sm: "0.95rem" },
                  color: theme.palette.text.primary,
                  py: { xs: 1, sm: 2 },
                  px: { xs: 0.5, sm: 2 },
                  verticalAlign: "top",
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                  backgroundColor: rowHoverBg,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    maxWidth: { xs: 140, sm: "none" },
                    minWidth: { xs: 120, sm: "auto" }
                  }}>
                    Independent Professional
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: 80, sm: "auto" },
                    minWidth: { xs: 70, sm: "auto" }
                  }}>
                    Hired At
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: 70, sm: "auto" },
                    minWidth: { xs: 60, sm: "auto" }
                  }}>
                    Price
                  </TableCell>
                  {isHourly && (
                    <TableCell sx={{
                      width: { xs: 70, sm: "auto" },
                      minWidth: { xs: 60, sm: "auto" }
                    }}>
                      Max Hour
                    </TableCell>
                  )}
                  <TableCell sx={{
                    width: { xs: 100, sm: "auto" },
                    minWidth: { xs: 90, sm: "auto" }
                  }}>
                    Start
                  </TableCell>
                  <TableCell sx={{
                    maxWidth: { xs: 200, sm: "none" },
                    minWidth: { xs: 100, sm: "auto" }
                  }}>
                    Status
                  </TableCell>
                  <TableCell align="right" sx={{
                    minWidth: { xs: 150, sm: "auto" },
                    width: { xs: 150, sm: "auto" }
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hiredCount === 0 && !loading && (
                  <TableRow>
                    <TableCell
                      colSpan={isHourly ? 7 : 6}
                      sx={{
                        textAlign: "center",
                        py: { xs: 4, sm: 6 },
                        color: subtleTextColor,
                        fontSize: { xs: "0.7rem", sm: "0.95rem" },
                      }}
                    >
                      No independent professionals hired yet.
                    </TableCell>
                  </TableRow>
                )}
                {bids?.map((job) => {
                  const status = getHiredTalentStatus(project, job);
                  const progress = getLastProgress(job);
                  const reviewForTalent = reviews?.find(
                    (it) =>
                      it.model_type === "user" && it.model_id === job.resource_id
                  );
                  const reviewForClient = reviews?.find(
                    (it) =>
                      it.model_type === "company" &&
                      it.created_by === job.resource_id
                  );
                  return (
                    <React.Fragment key={job.id}>
                      <TableRow
                        sx={{
                          "&:last-of-type .MuiTableCell-root": {
                            borderBottom:
                              project.status === ProjectStatus.COMPLETED
                                ? `1px solid ${borderColor}`
                                : "none",
                          },
                        }}
                      >
                        <TableCell sx={{ width: { xs: 140, md: 260 } }}>
                          <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} alignItems="center">
                            <Box>
                              <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" flexWrap="wrap">
                                <Link
                                  component="button"
                                  onClick={() => {
                                    setSelectedTalent(job.resource || null);
                                    setDrawerOpen(true);
                                  }}
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "1rem" },
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {job.resource?.full_name || "N/A"}
                                </Link>
                                <Rating
                                  value={job.resource?.total_rating ?? 0}
                                  readOnly
                                  precision={0.5}
                                  size="small"
                                  sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                                />
                              </Stack>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: subtleTextColor,
                                  display: "block",
                                  mt: 0.25,
                                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                }}
                              >
                                {[
                                  job.resource?.city,
                                  job.resource?.state,
                                  job.resource?.zip
                                ].filter(Boolean).join(", ") || "Location not available"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{
                          whiteSpace: "nowrap",
                          fontSize: { xs: "0.7rem", sm: "0.95rem" },
                          width: { xs: 80, sm: "auto" },
                        }}>
                          <Typography
                            component="span"
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.95rem" } }}
                          >
                            {job.hired_at ? dayjs(job.hired_at).format("MMM DD, YYYY") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                          {isHourly
                            ? job.rate !== null ? `$${job.rate}/hr` : "-"
                            : job.price !== null ? `$${job.price}` : "-"}
                        </TableCell>
                        {isHourly && (
                          <TableCell sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                            {job.max_hour !== null ? `${job.max_hour} hr` : "-"}
                          </TableCell>
                        )}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" }
                            }}
                          >
                            {job.start_date && job.timezone
                              ? formatWithTimezone(job.start_date, job.timezone)
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: { xs: 200, sm: 320 } }}>
                          <Stack spacing={{ xs: 0.5, sm: 1 }} alignItems="flex-start">
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                borderRadius: "8px",
                                px: 0.75,
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                            {!!status.helperText && (
                              <FormHelperText
                                sx={{
                                  mt: 0,
                                  color: subtleTextColor,
                                  lineHeight: 1.4,
                                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                }}
                              >
                                {status.helperText}
                              </FormHelperText>
                            )}
                            {progress?.status === ReportProgressStatus.REJECTED &&
                              !!progress.reason && (
                                <FormHelperText
                                  sx={{
                                    mt: 0,
                                    color: theme.palette.error.main,
                                    fontWeight: 500,
                                  }}
                                >
                                  Reason → {progress.reason}
                                </FormHelperText>
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: { xs: 150, sm: 340 } }}>
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={{ xs: 0.75, md: 2 }}
                            justifyContent="flex-end"
                            alignItems={{ xs: "stretch", md: "flex-start" }}
                          >
                            <Stack
                              direction="row"
                              spacing={{ xs: 0.5, sm: 1 }}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              rowGap={{ xs: 0.5, sm: 1 }}
                            >
                              {progress?.status === ReportProgressStatus.SUBMITTED && (
                                <>
                                  <Button
                                    color="success"
                                    variant="outlined"
                                    sx={actionButtonSx}
                                    onClick={() =>
                                      setApproveModal({ visible: true, progress })
                                    }
                                  >
                                    Work Approved
                                  </Button>
                                  <Button
                                    color="error"
                                    variant="outlined"
                                    sx={actionButtonSx}
                                    onClick={() =>
                                      setRejectModal({ visible: true, progress })
                                    }
                                  >
                                    Work Not Approved
                                  </Button>
                                </>
                              )}
                              {progress?.status !== ReportProgressStatus.APPROVED &&
                                progress?.status !== ReportProgressStatus.SUBMITTED && (
                                  <Button
                                    color="error"
                                    variant="outlined"
                                    size="small"
                                    sx={actionButtonSx}
                                    onClick={() =>
                                      setCancelTalentModal({ visible: true, job })
                                    }
                                  >
                                    Cancel
                                  </Button>
                                )}
                              {!!progress?.check_in && !progress?.check_out && (
                                <Button
                                  color="info"
                                  variant="text"
                                  sx={textButtonSx}
                                  onClick={() =>
                                    setCheckoutModal({ visible: true, progress })
                                  }
                                >
                                  Force Checkout
                                </Button>
                              )}
                              {progress?.status === ReportProgressStatus.APPROVED && (
                                <Button
                                  color="info"
                                  variant="outlined"
                                  size="small"
                                  sx={actionButtonSx}
                                  onClick={() =>
                                    setBonusModal({ visible: true, job })
                                  }
                                >
                                  $ Extra Spending
                                </Button>
                              )}
                            </Stack>

                            <Stack
                              spacing={{ xs: 0.5, sm: 1 }}
                              alignItems="flex-end"
                              sx={{ minWidth: { xs: "100%", md: 200 } }}
                            >
                              {!!job.notes && (
                                <Button
                                  startIcon={<NotesOutlined />}
                                  color="info"
                                  variant="outlined"
                                  sx={utilityButtonSx}
                                  onClick={() =>
                                    setFinalNotesModal({
                                      visible: true,
                                      notes: job.notes || null,
                                    })
                                  }
                                >
                                  Final Notes
                                </Button>
                              )}
                              <ButtonPhotos
                                projectId={project.id}
                                progressId={progress?.id!}
                                shouldRefresh={loading}
                                buttonProps={{ sx: utilityButtonSx }}
                              />
                              <Button
                                color="info"
                                variant="outlined"
                                startIcon={<History />}
                                sx={utilityButtonSx}
                                onClick={() => setLogModal({ visible: true, job })}
                              >
                                Log
                              </Button>
                            </Stack>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {project.status === ProjectStatus.COMPLETED && (
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.04),
                            "& .MuiTableCell-root": {
                              borderBottom: `1px dashed ${borderColor}`,
                            },
                          }}
                        >
                          <TableCell />
                          <TableCell colSpan={isHourly ? 3 : 2}>
                            <Stack spacing={{ xs: 0.75, sm: 1.25 }} alignItems="flex-start">
                              <Typography
                                variant="subtitle2"
                                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                              >
                                Your review for the independent professional
                              </Typography>
                              {!!reviewForTalent ? (
                                <Stack spacing={{ xs: 0.5, sm: 0.75 }} alignItems="flex-start">
                                  <Rating
                                    value={reviewForTalent.star}
                                    readOnly
                                    precision={0.5}
                                    size="small"
                                    sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: "pre-line",
                                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                    }}
                                  >
                                    {reviewForTalent.review}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Stack spacing={{ xs: 0.5, sm: 0.75 }} alignItems="flex-start">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: subtleTextColor,
                                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                    }}
                                  >
                                    No review yet.
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Star />}
                                    sx={actionButtonSx}
                                    onClick={() =>
                                      setReviewModal({ visible: true, job })
                                    }
                                  >
                                    Give Review
                                  </Button>
                                </Stack>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell colSpan={isHourly ? 3 : 3}>
                            <Stack spacing={{ xs: 0.75, sm: 1.25 }} alignItems="flex-start">
                              <Typography
                                variant="subtitle2"
                                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                              >
                                Independent professional's review for you
                              </Typography>
                              {!!reviewForClient ? (
                                <Stack spacing={{ xs: 0.5, sm: 0.75 }} alignItems="flex-start">
                                  <Rating
                                    value={reviewForClient.star}
                                    readOnly
                                    precision={0.5}
                                    size="small"
                                    sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: "pre-line",
                                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                    }}
                                  >
                                    {reviewForClient.review}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: subtleTextColor,
                                    fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                  }}
                                >
                                  No review yet.
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </GlassTable>
        </Box>
      </CardBase>
      <TalentDrawer
        forceRefresh={true}
        data={selectedTalent}
        open={drawerOpen}
        setOpen={setDrawerOpen}
      />
      <ConfirmModal
        open={approveModal.visible}
        title={`Approve work done by this Independent Professional?`}
        onClose={() => setApproveModal({ visible: false, progress: null })}
        onConfirm={handleApprove}
      />
      <LogModal
        open={logModal.visible}
        job={logModal.job || null}
        onClose={() => setLogModal({ visible: false, job: null })}
      />
      <GiveReviewModal
        open={reviewModal.visible}
        job={reviewModal.job}
        onClose={() => setReviewModal({ visible: false, job: null })}
        onSubmit={handleReview}
      />
      <ForceCheckoutModal
        open={checkoutModal.visible}
        progress={checkoutModal.progress}
        onClose={() => setCheckoutModal({ visible: false, progress: null })}
        onSubmit={handleForceCheckout}
      />
      <ModalBonusForm
        open={bonusModal.visible}
        job={bonusModal.job}
        onClose={() => setBonusModal({ visible: false, job: null })}
      />
      <ModalCancelTalent
        open={cancelTalentModal.visible}
        onClose={() => setCancelTalentModal({ visible: false, job: null })}
        job={cancelTalentModal.job}
      />
      <ModalRejectJob
        open={rejectModal.visible}
        onClose={() => setRejectModal({ visible: false, progress: null })}
        progress={rejectModal.progress}
        projectId={project.id}
      />
      <FinalNotesModal
        open={finalNotesModal.visible}
        onClose={() => setFinalNotesModal({ visible: false, notes: null })}
        notes={finalNotesModal.notes}
      />
    </Box>
  );
};
export default TableHireds;
